/**
 * AffiliatePortalLayout — shell for every signed-in and auth screen of the
 * affiliate portal.
 *
 * Not `AppShell`: an affiliate is not a member, so the member sidebar would list
 * destinations this session cannot open. The header carries only the brand and
 * whatever actions the current screen owns.
 */
import type { PropsWithChildren, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { LogoutIcon } from '../../components/ui/icons';
import { AFFILIATE_ROUTES } from './affiliateRoutes';
import '../../components/layout/authFlow.css';
import './affiliatePortal.css';

interface AffiliatePortalLayoutProps extends PropsWithChildren {
  /** Rendered on the right of the header — screen-owned actions. */
  headerActions?: ReactNode;
  /** Vertically centres the content, for the single-card auth screens. */
  centered?: boolean;
}

export function AffiliatePortalLayout({
  children,
  headerActions,
  centered = false,
}: AffiliatePortalLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSignedIn, signOut } = useAffiliateSession();

  // In the header rather than on the profile screen so it stays reachable when
  // a portal screen can only render an error — otherwise a rejected token would
  // leave the affiliate with no way to sign back in.
  const handleSignOut = async () => {
    navigate(AFFILIATE_ROUTES.LANDING, { replace: true });
    await signOut();
  };

  return (
    <div className="affiliate-portal">
      <header className="affiliate-portal__header">
        <Link className="affiliate-portal__brand" to="/">
          <BrandLogo />
        </Link>
        {(headerActions || isSignedIn) && (
          <div className="affiliate-portal__header-actions">
            {headerActions}
            {isSignedIn && (
              <button
                type="button"
                className="affiliate-portal__header-link"
                onClick={handleSignOut}
              >
                <LogoutIcon size={18} aria-hidden />
                {t('affiliate.profile.logout')}
              </button>
            )}
          </div>
        )}
      </header>
      <main
        className={`affiliate-portal__main${
          centered ? ' affiliate-portal__main--centered' : ''
        }`}
      >
        {children}
      </main>
      <footer className="affiliate-portal__footer">
        {t('affiliate.footer.copyright', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
}
